/**
 * @file Expert dashboard UI — pages/Dashboard/Expert/Submissions/ExpertReviewSubmissions.tsx.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";
import {
  CementBoxPlotChart,
  StageBarChart,
  type CementBoxPlotMetric,
  type CementBoxPlotSlotSelection,
} from "../../../../components";
import {
  buildExpertFlaggedStageBarChartData,
  computeStageBarChartYMax,
  getStageBarChartIndividualLegendLabel,
} from "../../../../utils/expertFlaggedStageBarChart";
import { useExpertSubmissions } from "./hooks/useExpertSubmissions";
import SubmissionsTable from "./components/SubmissionsTable";
import EmptyState from "./components/EmptyState";
import LoadingState from "./components/LoadingState";
import NoMaterialState from "./components/NoMaterialState";
import AnalysisBanner from "../../../../components/common/AnalysisBanner";
import ExpertChartSlotBanner from "../components/ExpertChartSlotBanner";
import ExpertDatasetSearchBar from "../components/ExpertDatasetSearchBar";
import {
  filterExpertDatasetTableRows,
  filterRowsByChartSlot,
  splitChartSlotCodesByReviewQueue,
  toggleExpertChartSlotSelection,
} from "../../../../services/expertDatasetService";
import "./ExpertReviewSubmissions.css";

interface Props {
  materialId?: number | null;
  onBack?: () => void;
  onOpenReview?: (submissionId: string) => void;
}

const ExpertReviewSubmissions: React.FC<Props> = ({ materialId, onOpenReview }) => {
  const { rows, chartDatasets, reviewHighlightIds, materialName, loading, error } =
    useExpertSubmissions(materialId);
  const [searchTerm, setSearchTerm] = useState("");
  const [chartSlotSelection, setChartSlotSelection] =
    useState<CementBoxPlotSlotSelection | null>(null);
  const [metric, setMetric] = useState<CementBoxPlotMetric>("carbon");
  const [selectedStageChartSubmissionId, setSelectedStageChartSubmissionId] =
    useState<string | null>(null);
  const chartSlotToastReady = useRef(false);
  const stageChartRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    setSearchTerm("");
    setChartSlotSelection(null);
    setSelectedStageChartSubmissionId(null);
    chartSlotToastReady.current = false;
  }, [materialId]);

  useEffect(() => {
    setSelectedStageChartSubmissionId(null);
  }, [chartSlotSelection]);

  useEffect(() => {
    if (!chartSlotToastReady.current) {
      chartSlotToastReady.current = true;
      return;
    }

    if (chartSlotSelection) {
      toast.info(
        "Chart filter applied. Select a submission code to open the review graph.",
        { toastId: "expert-chart-slot-visible" }
      );
      return;
    }

    toast.info("Chart filter cleared. Filtered submission list is hidden.", {
      toastId: "expert-chart-slot-hidden",
    });
  }, [chartSlotSelection]);

  const prevStageChartSubmissionRef = useRef<string | null>(null);
  useEffect(() => {
    const prev = prevStageChartSubmissionRef.current;
    prevStageChartSubmissionRef.current = selectedStageChartSubmissionId;
    if (selectedStageChartSubmissionId && !prev) {
      toast.info("Review of flagged dataset is now visible.", {
        toastId: "expert-stage-chart-visible",
      });
    }
  }, [selectedStageChartSubmissionId]);

  useEffect(() => {
    if (!selectedStageChartSubmissionId) return;
    const frameId = window.requestAnimationFrame(() => {
      stageChartRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
    });
    return () => window.cancelAnimationFrame(frameId);
  }, [selectedStageChartSubmissionId]);

  const handleChartSlotClick = useCallback((detail: CementBoxPlotSlotSelection) => {
    setChartSlotSelection((prev) => toggleExpertChartSlotSelection(prev, detail));
  }, []);

  const clearChartSlotSelection = useCallback(() => setChartSlotSelection(null), []);

  const searchFilteredRows = useMemo(
    () => filterExpertDatasetTableRows(rows, searchTerm),
    [rows, searchTerm]
  );

  const rowsFilteredByChart = useMemo(
    () => filterRowsByChartSlot(searchFilteredRows, chartSlotSelection),
    [searchFilteredRows, chartSlotSelection]
  );

  const chartSlotCodeGroups = useMemo(() => {
    if (!chartSlotSelection) return null;
    return splitChartSlotCodesByReviewQueue(chartSlotSelection, reviewHighlightIds);
  }, [chartSlotSelection, reviewHighlightIds]);

  const selectedStageChartSubmissionCode = useMemo(() => {
    if (!selectedStageChartSubmissionId || !chartSlotCodeGroups) return null;
    const match = chartSlotCodeGroups.allEntries.find(
      (entry) => entry.id === selectedStageChartSubmissionId
    );
    return match?.code ?? selectedStageChartSubmissionId;
  }, [selectedStageChartSubmissionId, chartSlotCodeGroups]);

  const stageBarChartColumnDatasets = useMemo(() => {
    if (!chartSlotSelection) return [];
    const idSet = new Set(chartSlotSelection.datasetIds.map(String));
    return chartDatasets.filter((dataset) => idSet.has(String(dataset.id)));
  }, [chartSlotSelection, chartDatasets]);

  const stageBarChartAverageData = useMemo(
    () =>
      buildExpertFlaggedStageBarChartData({
        allDatasets: chartDatasets,
        metric,
        selectedDatasetId: null,
      }),
    [chartDatasets, metric]
  );

  const stageBarChartData = useMemo(
    () =>
      buildExpertFlaggedStageBarChartData({
        allDatasets: chartDatasets,
        metric,
        selectedDatasetId: selectedStageChartSubmissionId,
      }),
    [chartDatasets, metric, selectedStageChartSubmissionId]
  );

  const stageBarChartYMax = useMemo(
    () =>
      computeStageBarChartYMax({
        chartPoints: stageBarChartAverageData,
        columnDatasets: stageBarChartColumnDatasets,
        metric,
      }),
    [stageBarChartAverageData, stageBarChartColumnDatasets, metric]
  );

  const stageBarIndividualLegend = useMemo(
    () => getStageBarChartIndividualLegendLabel(selectedStageChartSubmissionCode),
    [selectedStageChartSubmissionCode]
  );

  const tableEmptyMessage = useMemo(() => {
    if (
      chartSlotSelection &&
      rowsFilteredByChart.length === 0 &&
      rows.length > 0
    ) {
      return "No datasets in this chart column match your current search. Clear the chart filter or adjust your search.";
    }
    return "No datasets match your search. Try a different submission code, contributor name, or email.";
  }, [chartSlotSelection, rowsFilteredByChart.length, rows.length]);

  if (!materialId) {
    return <NoMaterialState />;
  }

  if (loading) {
    return <LoadingState />;
  }

  if (error) {
    return (
      <div className="background-form-container analysis-shell">
        <AnalysisBanner title="Review Submissions" />
        <p className="expert-info-text" style={{ color: "#d32f2f" }}>
          {error}
        </p>
      </div>
    );
  }

  return (
    <div className="background-form-container analysis-shell">
      <AnalysisBanner
        title={`Review Submissions: ${materialName || `Material #${materialId}`}`}
      />
      <div className="expert-container expert-review-layout">
        {(chartDatasets?.length ?? 0) === 0 && rows.length === 0 ? (
          <EmptyState />
        ) : (
          <>
            {(chartDatasets?.length ?? 0) > 0 && (
              <div className="expert-graphs expert-review-stack">
                <div className="expert-review-panel graph-card data-visualisation-wrapper">
                  <h2 className="material-overview-graph-title">
                    Comparison of embodied impacts across categorical tiers
                  </h2>
                  <p className="material-overview-graph-hint material-overview-graph-hint--clickable">
                    Click any chart column to filter submissions in that group (same
                    sub-type, technology, time bucket, and metric as the chart).
                    Values outside the whiskers appear as dots (salmon = all datasets,
                    black = flagged review queue). Then click a submission code in the
                    banner below to open the stage comparison chart. Click the column
                    again to clear.
                  </p>
                  <CementBoxPlotChart
                    datasets={chartDatasets}
                    showFilters
                    parentMaterialName={materialName}
                    interactiveSlotSelection
                    onSlotClick={handleChartSlotClick}
                    onInteractiveMetricChange={clearChartSlotSelection}
                    highlightDatasetIds={rows.length > 0 ? reviewHighlightIds : null}
                    showHighlightLegend={rows.length > 0}
                    metric={metric}
                    onMetricChange={setMetric}
                  />
                </div>
              </div>
            )}

            {rows.length > 0 && onOpenReview ? (
              <div className="expert-review-stack">
                {chartSlotSelection ? (
                  <ExpertChartSlotBanner
                    chartSlotSelection={chartSlotSelection}
                    visibleCount={rowsFilteredByChart.length}
                    visibleSubmissionCodes={rowsFilteredByChart.map(
                      (r) => r.submissionCode || r.submissionId
                    )}
                    codeGroups={chartSlotCodeGroups}
                    selectedSubmissionId={selectedStageChartSubmissionId}
                    onSelectSubmissionId={setSelectedStageChartSubmissionId}
                    onClear={clearChartSlotSelection}
                  />
                ) : null}
                {selectedStageChartSubmissionId ? (
                  <div
                    ref={stageChartRef}
                    className="expert-review-panel graph-card stage-chart"
                  >
                    <StageBarChart
                      metric={metric}
                      data={stageBarChartData}
                      individualLegendLabel={stageBarIndividualLegend}
                      yMax={stageBarChartYMax}
                    />
                  </div>
                ) : null}
                <div className="expert-review-panel expert-review-search-panel">
                  <ExpertDatasetSearchBar value={searchTerm} onChange={setSearchTerm} />
                </div>
                <SubmissionsTable
                  rows={rowsFilteredByChart}
                  onOpenReview={onOpenReview}
                  emptyMessage={tableEmptyMessage}
                />
              </div>
            ) : rows.length === 0 && (chartDatasets?.length ?? 0) > 0 ? (
              <p className="expert-info-text" role="status">
                Nothing in your review queue for this material. The chart above shows all datasets for
                context.
              </p>
            ) : null}
          </>
        )}
      </div>
    </div>
  );
};

/** React component — Expert Review Submissions. */
export default ExpertReviewSubmissions;
